import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import type { LineType } from './agreements.ts';
import { ApiProblem } from './me.ts';

// CB-REQ INV-A01 – INV-A03 (v1.940). Shapes come from the generated contract. Cost fields are
// optional there because the API leaves them out below the price line (INV-10); the screens show
// what arrives and do not decide for themselves who may see it.

export type Product = components['schemas']['ProductView'];
export type ProductDetail = components['schemas']['ProductDetailView'];
export type Asset = components['schemas']['AssetView'];
export type Category = components['schemas']['CategoryView'];
export type AssetStatus = Asset['status'];

type Money = { amountMinor: string; currency: 'AUD' };

export type NewProduct = {
  name: string;
  tracking: 'serialised' | 'bulk' | 'service';
  categoryId?: string;
  manufacturer?: string;
  model?: string;
  replacementCost?: Money;
  openingQuantity?: number;
  /** INV-A14 at v1.982: the venue that owns it, for a venue's own equipment. */
  ownerVenueId?: string;
};

export type NewAsset = {
  serialNumber?: string;
  assetTag?: string;
  status?: 'in_stock' | 'in_repair' | 'quarantined';
  purchasedOn?: string;
  warrantyExpiresOn?: string;
  purchasePrice?: Money;
  supplier?: string;
  replacementCost?: Money;
};

/** INV-A03: the statuses this screen may set. The rest belong to book-out and transfers. */
export const SETTABLE_STATUSES = [
  'in_stock',
  'in_repair',
  'quarantined',
  'lost',
  'retired',
] as const;
export type SettableStatus = (typeof SETTABLE_STATUSES)[number];

export const STATUS_LABELS: Record<AssetStatus, string> = {
  in_stock: 'In stock',
  staged: 'Staged',
  on_job: 'On a job',
  in_transit: 'In transit',
  in_repair: 'In repair',
  quarantined: 'Quarantined',
  lost: 'Lost',
  retired: 'Retired',
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const productsQuery = queryOptions({
  queryKey: ['products'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/products');
    if (error) throw problem(error, 'Could not load the products.');
    return data;
  },
});

export const productQuery = (id: string) =>
  queryOptions({
    queryKey: ['products', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/products/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that product.');
      return data;
    },
  });

export const categoriesQuery = queryOptions({
  queryKey: ['product-categories'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/product-categories');
    if (error) throw problem(error, 'Could not load the categories.');
    return data;
  },
});

export async function createCategory(input: { name: string; parentId?: string }) {
  const { data, error } = await api.POST('/v1/product-categories', { body: input });
  if (error) throw problem(error, 'Could not save that category.');
  return data;
}

export async function createProduct(input: NewProduct) {
  const { data, error } = await api.POST('/v1/products', { body: input });
  if (error) throw problem(error, 'Could not save that product.');
  return data;
}

export async function createAsset(productId: string, input: NewAsset) {
  const { data, error } = await api.POST('/v1/products/{id}/assets', {
    params: { path: { id: productId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not add that serial.');
  return data;
}

export async function setAssetStatus(assetId: string, status: SettableStatus) {
  const { data, error } = await api.POST('/v1/assets/{id}/status', {
    params: { path: { id: assetId } },
    body: { status },
  });
  if (error) throw problem(error, 'Could not change that status.');
  return data;
}

/** A correction: a key left out is unchanged, a key set to null is cleared. */
export type ProductChanges = Partial<{
  name: string;
  categoryId: string | null;
  manufacturer: string | null;
  model: string | null;
  weightGrams: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  countryOfOrigin: string | null;
  replacementCost: Money | null;
  /** FIN-17 at v1.988: how its quote lines are typed for venue margins. */
  lineType: LineType;
  /** OPS-01 (crew part 4A): the crew role a labour line's task asks for. */
  crewRoleId: string | null;
}>;

export type AssetChanges = Partial<{
  /** VEN-03 at v1.986: a venue's unit's room. */
  installedRoomId: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  purchasedOn: string | null;
  warrantyExpiresOn: string | null;
  purchasePrice: Money | null;
  supplier: string | null;
  replacementCost: Money | null;
}>;

export async function updateProduct(id: string, changes: ProductChanges) {
  const { data, error } = await api.PATCH('/v1/products/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save those changes.');
  return data;
}

/** VEN-03 at v1.986: a venue's counted item split by its rooms, replacing the split it had. */
export async function setInstalledRooms(
  productId: string,
  rooms: Array<{ roomId: string; quantity: number }>,
) {
  const { data, error } = await api.PUT('/v1/products/{id}/installed-rooms', {
    params: { path: { id: productId } },
    body: { rooms },
  });
  if (error) throw problem(error, 'Could not save where it is installed.');
  return data;
}

export async function updateAsset(id: string, changes: AssetChanges) {
  const { data, error } = await api.PATCH('/v1/assets/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save those changes.');
  return data;
}

export async function removeAsset(id: string) {
  const { error } = await api.DELETE('/v1/assets/{id}', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not remove that unit.');
}

// INV-A12 at v1.955: where stock is kept, moving it, and (INV-A02) correcting a bulk count.
export type StockLocation = components['schemas']['StockLocationView'];

export const stockLocationsQuery = queryOptions({
  queryKey: ['stock-locations'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/stock-locations');
    if (error) throw problem(error, 'Could not load the stock locations.');
    return data;
  },
});

/** INV-A09 at v1.986: a location becomes home from a day, today or later. */
export async function addHomeLocation(locationId: string, from: string) {
  const { data, error } = await api.POST('/v1/home-locations', { body: { locationId, from } });
  if (error) throw problem(error, 'Could not make that the home location.');
  return data;
}

export async function moveBulk(
  productId: string,
  input: { from: string; to: string; quantity: number; reason: string },
) {
  const { data, error } = await api.POST('/v1/products/{id}/moves', {
    params: { path: { id: productId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not move that stock.');
  return data;
}

export async function moveUnits(
  productId: string,
  input: { to: string; assetIds: string[]; reason: string },
) {
  const { data, error } = await api.POST('/v1/products/{id}/unit-moves', {
    params: { path: { id: productId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not move those units.');
  return data;
}

export async function adjustStock(
  productId: string,
  input: { locationId: string; delta: number; reason: string },
) {
  const { data, error } = await api.POST('/v1/products/{id}/adjustments', {
    params: { path: { id: productId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not change that count.');
  return data;
}

/** VEN-03 at v1.986: the venue's own gear installed in each of its rooms, by room id. */
export type InstalledGear = components['schemas']['InstalledGearView'];

export const installedGearQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['installed-gear', venueId],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/installed-gear', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the venue’s installed gear.');
      return data;
    },
  });
